import classesFeature from "../../assets/classes/decorating-feature.webp";
import cakeDecoratingFeature from "../../assets/classes/cake-decorating-feature.webp";
import cupcakeDecoratingFeature from "../../assets/classes/cupcake-decorating-feature.webp";
import cookieDecoratingFeature from "../../assets/classes/cookie-decorating-feature.webp";
import fondantDecoratingFeature from "../../assets/classes/fondant-decorating-feature.webp";
import classesPdf from "../../assets/classes/Connies-Cakerie-Decorating-Classes.pdf";
import Offering from "../home/Offering";

import { PDFIcon } from "../../assets/global/Icons";

import { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./Classes.scss";
import ClassSlot from "./ClassSlot";
import ClassBookingForm from "./ClassBookingForm";
import ClassRequestForm from "./ClassRequestForm";

import PaymentModal from "../../common/PaymentModal";
import {
  PAYMENT_METHOD_IDS,
  PAYMENT_METHODS,
  type PaymentMethod,
} from "../../common/paymentConfig";
import { scrollToHashSection, scrollToSection } from "../../common/scrollUtils";
import { SheetProperties } from "../../types";

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

type ClassSession = {
  time: string;
  type: string;
  audience: string;
  spots: number;
};

const GROUP_CLASS_CAPACITY = 6;
const LOW_SPOTS_THRESHOLD = 2;
const CLASS_BLOCK_ROWS = 5;
const API_KEY = "AIzaSyBIHXbYUbv7R1j-86Vy3yCgGIKmUpN6u0M";
const SPREADSHEET_ID = "17_70dU6P0OAeIfCx3sPznO5qoAC5Mq3qzUP6HCj3X80";

function formatDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isPastDate(date: Date) {
  return startOfDay(date) < startOfDay(new Date());
}

function cellText(value: unknown) {
  if (Array.isArray(value)) {
    return String(value[0] ?? "");
  }
  return String(value ?? "");
}

function parseSpots(value: string) {
  const parsedSpots = Number(value);
  return value === "" || !Number.isFinite(parsedSpots)
    ? GROUP_CLASS_CAPACITY
    : parsedSpots;
}

function parseClassSessions(values: unknown[][]): ClassSession[] {
  const rows = values.map((row) => cellText(row).trim());
  const sessions: ClassSession[] = [];
  let index = 0;

  while (index < rows.length) {
    while (index < rows.length && rows[index] === "") {
      index += 1;
    }
    if (index >= rows.length) {
      break;
    }

    const block = rows.slice(index, index + CLASS_BLOCK_ROWS);
    sessions.push({
      time: block[0] ?? "",
      type: block[1] ?? "",
      audience: block[2] ?? "",
      spots: parseSpots(block[4] ?? ""),
    });
    index += CLASS_BLOCK_ROWS;
  }

  return sessions;
}

function getTileClassForSpots(spotsList: number[]) {
  const scheduledSpots = spotsList.filter(
    (spots) => spots > 0 && spots < GROUP_CLASS_CAPACITY,
  );
  if (scheduledSpots.length > 0) {
    return Math.min(...scheduledSpots) <= LOW_SPOTS_THRESHOLD
      ? "calendar-tile--low"
      : "calendar-tile--open";
  }
  if (spotsList.some((spots) => spots >= GROUP_CLASS_CAPACITY)) {
    return "calendar-tile--request";
  }
  return "calendar-tile--full";
}

function getTileClassName(
  date: Date,
  classByDate: Record<string, ClassSession[]>,
) {
  if (isPastDate(date)) {
    return null;
  }

  const sessions = classByDate[formatDateKey(date)];
  if (!sessions?.length) {
    return null;
  }

  return getTileClassForSpots(sessions.map((session) => session.spots));
}

function Classes() {
  const [selectedDate, setSelectedDate] = useState<Value>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [classByDate, setClassByDate] = useState<
    Record<string, ClassSession[]>
  >({});
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(0);
  const [partySize, setPartySize] = useState(1);
  const [paymentModal, setPaymentModal] = useState<PaymentMethod | null>(null);

  useEffect(() => {
    const spreadsheetUrl = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}?key=${API_KEY}`;
    let cancelled = false;

    async function loadClassDays() {
      try {
        const resp = await fetch(spreadsheetUrl);
        if (!resp.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await resp.json();
        const titles: string[] = data.sheets.map(
          (sheet: { properties: SheetProperties }) => sheet.properties.title,
        );

        if (titles.length === 0 || cancelled) {
          return;
        }

        const chunkSize = 40;
        const valueRanges: { values?: unknown[][] }[] = [];

        for (let i = 0; i < titles.length; i += chunkSize) {
          const chunk = titles.slice(i, i + chunkSize);
          const params = new URLSearchParams({ key: API_KEY });
          chunk.forEach((title) => params.append("ranges", `${title}!A1:A30`));

          const batchResp = await fetch(
            `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values:batchGet?${params.toString()}`,
          );
          if (!batchResp.ok) {
            throw new Error("Network response was not ok");
          }
          const batchData = await batchResp.json();
          valueRanges.push(...(batchData.valueRanges ?? []));
        }

        if (cancelled) {
          return;
        }

        const nextClassByDate: Record<string, ClassSession[]> = {};
        titles.forEach((title, index) => {
          const sessions = parseClassSessions(valueRanges[index]?.values ?? []);
          nextClassByDate[title] =
            sessions.length > 0
              ? sessions
              : [
                  {
                    time: "",
                    type: "",
                    audience: "",
                    spots: GROUP_CLASS_CAPACITY,
                  },
                ];
        });
        setClassByDate(nextClassByDate);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching spreadsheet:", error);
        }
      }
    }

    loadClassDays();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setShowConfirmation(false);
    setSelectedSessionIndex(0);
    setPartySize(1);
  }, [selectedDate]);

  useEffect(() => {
    if (!showConfirmation) {
      return;
    }
    scrollToSection("confirm-class-section");
  }, [showConfirmation]);

  const selectedSingleDate =
    selectedDate && !(selectedDate instanceof Array) ? selectedDate : null;
  const selectedSessions = selectedSingleDate
    ? (classByDate[formatDateKey(selectedSingleDate)] ?? [])
    : [];
  const selectedSession =
    selectedSessions[selectedSessionIndex] ?? selectedSessions[0];
  const selectedSpots = selectedSession?.spots ?? GROUP_CLASS_CAPACITY;
  const isRequest = selectedSpots >= GROUP_CLASS_CAPACITY;
  const isFull = selectedSpots <= 0;
  const classDate = selectedSingleDate
    ? new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(selectedSingleDate)
    : "";

  return (
    <div className="wrapper">
      <h1>Join a decorating class</h1>
      <section className="flex-container">
        <img
          src={classesFeature}
          alt="Group cookie decorating class"
          className="page-feature-img"
        />
        <div className="page-feature-desc">
          <p>
            Ready to elevate your cake decorating prowess? Explore a diverse
            array of modules at Connie's Cakerie, ranging from cake and cupcake
            decoration to cookie artistry and fondant mastery. Whether you're a
            novice eager to learn, a seasoned decorator looking to enhance your
            skills, or a Cake4Kids volunteer baker, these courses promise a fun
            and rewarding experience.
          </p>
          <p>
            I am a certified Wilton instructor with over 10 years of experience.
            Having crafted hundreds of intricate creations and mentored students
            of all ages and skill levels, I am excited to share my wealth of
            knowledge.
          </p>
          <p>
            All classes are 2 hours long, and sessions are held in a private
            home in Mountain View. Remote or in-home classes can also be
            accommodated (price dependent on location).
          </p>
          <a href="#book-class-section" onClick={scrollToHashSection}>
            <button className="btn">Book a class</button>
          </a>
        </div>
      </section>

      <hr />

      <div className="classes-overview-container">
        <div className="pdf-link-container">
          <PDFIcon className="pdf-icon" />
          <a href={classesPdf} target="_blank" rel="noopener noreferrer">
            View PDF with detailed class descriptions
          </a>
        </div>
        <div className="class-metric">
          <span className="class-metric__value">2 hours</span>
          <span className="class-metric__label">class duration</span>
        </div>
        <div className="class-metric">
          <span className="class-metric__value">3 levels</span>
          <span className="class-metric__label">course I, II, III</span>
        </div>
        <div className="class-metric">
          <span className="class-metric__value">4 modules</span>
          <span className="class-metric__label">
            cake/cupcake/cookie/fondant
          </span>
        </div>
      </div>

      <hr />

      <section>
        <h2>Available decorating class modules</h2>
        <div className="offering-container">
          <Offering
            imgSrc={cakeDecoratingFeature}
            imgAlt="Cake with fresh strawberries and meringue"
            heading="cake decorating"
            subheading="(courses I-III)"
            desc="Learn essential skills to decorate round cakes with smooth, sharp edges. Take home a beautifully customized cake to share with family and friends!"
            disableLink
          />
          <Offering
            imgSrc={cupcakeDecoratingFeature}
            imgAlt="Assorted floral cupcakes"
            heading="cupcake decorating"
            subheading="(courses I-III)"
            desc="From basic piping techniques to advanced embellishments, you will learn to transform simple cupcakes into edible works of art."
            disableLink
          />
        </div>
        <div className="offering-container">
          <Offering
            imgSrc={cookieDecoratingFeature}
            imgAlt="Science themed cookies"
            heading="cookie decorating"
            subheading="(courses I-III)"
            desc="This module will guide you through harnessing the power of royal icing to create intricate detailing and achieve that smooth and glossy finish on cookies."
            disableLink
          />
          <Offering
            imgSrc={fondantDecoratingFeature}
            imgAlt="Cocomelon Two-Tiered Cake"
            heading="fondant"
            subheading="(courses I-III)"
            desc="Dive into the world of fondant in this comprehensive module! You will master smooth fondant application to cakes and use various tools to sculpt designs."
            disableLink
          />
        </div>
      </section>

      <hr />

      <section id="book-class-section" className="slide-down">
        <h2>Book a session</h2>
        <h4>Pricing information</h4>
        <p>
          <strong>Group sessions</strong>: $95/person (~4-6 students)
          <br />
          <strong>Private sessions</strong>: $250 (1 student) / $350 (2
          students) / $380 (3 students)
          <br />
          <strong>Corporate workshops</strong>:{" "}
          <a href="#class-inquiry-section" onClick={scrollToHashSection}>
            Send a general inquiry
          </a>{" "}
          to discuss workshop customizations.
        </p>
        <p>
          Please use the calendar below to book your next class. If there is a
          class scheduled, you will be able to book directly. You also have the
          option to request a booking date, but please note that a minimum of{" "}
          <strong>four</strong> students are required to start a group session.
          If the minimum is not met 1-2 weeks before the class date, the class
          will unfortunately have to be rescheduled.
        </p>
        <p>
          Don&apos;t see a date that works, or not ready to book yet?{" "}
          <a href="#class-inquiry-section" onClick={scrollToHashSection}>
            Send a general inquiry
          </a>{" "}
          instead.
        </p>

        <div className="flex-container">
          <div className="booking-calendar">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              minDate={new Date()}
              tileDisabled={({ date }) => !classByDate[formatDateKey(date)]}
              tileClassName={({ date }) => getTileClassName(date, classByDate)}
            />
            <ul className="calendar-legend">
              <li className="calendar-legend__item calendar-legend__item--request">
                Open to request
              </li>
              <li className="calendar-legend__item calendar-legend__item--open">
                Spots available
              </li>
              <li className="calendar-legend__item calendar-legend__item--low">
                2 or fewer spots
              </li>
            </ul>
          </div>
          {selectedSingleDate && (
            <div className="booking">
              <span className="booking__date">{classDate}</span>
              {selectedSessions.map((session, index) => (
                <ClassSlot
                  key={`${selectedSingleDate.getTime()}-${index}`}
                  time={session.time}
                  type={session.type}
                  audience={session.audience}
                  spots={session.spots}
                  isRequest={session.spots >= GROUP_CLASS_CAPACITY}
                  onBook={(size) => {
                    setSelectedSessionIndex(index);
                    setPartySize(size);
                    setShowConfirmation(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section
        id="confirm-class-section"
        className={`booking-confirmation ${showConfirmation ? "" : "hide"}`}
      >
        <h2>
          {isFull
            ? "Join the waitlist"
            : isRequest
              ? "Request this time"
              : "Confirm your spot"}
        </h2>
        {selectedSession?.time ? (
          <h4 className="margin-bottom-sm">{selectedSession.time}</h4>
        ) : null}
        {!isFull ? (
          <p className="margin-bottom-sm">Party size: {partySize}</p>
        ) : null}
        <p>
          {isFull ? (
            "This class is fully booked. Leave your info and I will reach out if a spot opens up."
          ) : (
            <>
              Please note that a 50% deposit will be required
              {isRequest ? " to hold this date" : " upon booking"}. Use the
              payment options below to make your deposit. A confirmation email
              will be sent to you upon receipt of your deposit for more details
              on class preparation and location.
            </>
          )}
        </p>

        <div className="booking-container">
          <ClassBookingForm
            key={`${selectedSingleDate ? formatDateKey(selectedSingleDate) : "none"}-${selectedSessionIndex}`}
            classDate={classDate}
            preferredDate={
              selectedSingleDate ? formatDateKey(selectedSingleDate) : ""
            }
            sessionTime={selectedSession?.time ?? ""}
            partySize={partySize}
            isRequest={isRequest}
            isFull={isFull}
            onCancel={() => setShowConfirmation(false)}
          />

          {!isFull && (
            <div className="booking-container__payment">
              <h2>Payment Options</h2>
              {PAYMENT_METHOD_IDS.map((method, index) => {
                const payment = PAYMENT_METHODS[method];

                return (
                  <div
                    key={method}
                    className={`booking-payment-option${index > 0 ? " margin-top-sm" : ""}`}
                  >
                    {payment.name}
                    <br />
                    {payment.detail}
                    <br />
                    <button
                      type="button"
                      className="payment-qr-link"
                      onClick={() => setPaymentModal(method)}
                    >
                      View QR Code
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <hr />

      <section id="class-inquiry-section">
        <h2>Send a general inquiry</h2>
        <p>
          Not ready to book a listed date? Fill out the form below and I will
          follow up with availability.
        </p>
        <ClassRequestForm />
      </section>
      <PaymentModal
        method={paymentModal}
        onClose={() => setPaymentModal(null)}
      />
    </div>
  );
}

export default Classes;
